<template>
  <div>
    <header class="vff-header">
      <div class="f-container">
        <a href="http://www.survein.com" v-show="displayLogo"
          ><img src="../assets/img/logo.png" alt="logo" class="f-logo"
        /></a>
      </div>
      <div class="score-wrapper" v-if="template === 'quiz'">
        <icon name="trophy" />
        <span class="score">{{ totalScore }}</span>
      </div>
    </header>

    <div
      class="username-quiz"
      v-if="showUsername && questions.length && acceptingResponse"
    >
      <span class="name-quiz">{{ $t("message.app.enterYourName") }}</span>
      <input
        class="username-input"
        type="text"
        v-model="username"
        :placeholder="this.$t('message.app.writeYourName')"
      />
      <button
        @click.prevent="showFlowform"
        class="o-btn-action"
        type="button"
        ref="button"
        href="#"
      >
        <span>{{ $t("message.app.startQuiz") }}</span>
      </button>
    </div>

    <flow-form
      v-if="questions.length && acceptingResponse && !showUsername"
      ref="flowform"
      v-on:complete="onComplete"
      v-on:submit="onSubmit"
      v-on:add-score-point="addScorePoint"
      v-on:add-correct-answer="addTotalCorrectAnswer"
      v-on:add-wrong-answer="addTotalWrongAnswer"
      v-bind:questions="questions"
      v-bind:language="language"
      v-bind:standalone="true"
      v-bind:end-page="endPage"
      v-bind:is-uploading-file="isUploadingFile"
      v-bind:is-uploading-error="isUploadingError"
      v-bind:total-score="totalScore"
      v-bind:template="template"
    >
    </flow-form>

    <div v-if="isLoading">
      <div class="loader">
        <img
          src="https://s3.ap-southeast-1.amazonaws.com/assets.survein.com/loading.gif"
          alt="Survein"
        />
      </div>
    </div>

    <div v-if="!isLoading && !questions.length && acceptingResponse" class="not-found">
      <icon name="not-found" />
      <h1 class="m-8">{{ $t("message.app.pageNotFound") }}</h1>
      <span>{{ $t("message.app.pageNotFoundDesc") }}</span>
      <a class="button-homepage" href="http://survein.com">
        {{ $t("message.app.goToHomepage") }}
      </a>
    </div>

    <template v-if="!isLoading && !acceptingResponse">
      <div class="not-found">
        <icon name="closed-response" />
        <h1 class="m-8">{{ $t("message.app.formClosed", { formTitle }) }}</h1>
        <span>{{ $t("message.app.formClosedDesc") }}</span>
        <a class="button-homepage" href="http://survein.com">
          {{ $t("message.app.goToSurvein") }}
        </a>
      </div>
      <p class="accept-link">
        {{ $t("message.app.formCreated") }}
        <a href="http://survein.com">{{ $t("message.app.createForm") }}</a>
      </p>
    </template>
  </div>
</template>

<script>
import FlowForm from "../../src/components/FlowForm.vue";
import QuestionModel, {
  QuestionType,
  ChoiceOption
} from "../../src/models/QuestionModel";
import LanguageModel from "../../src/models/LanguageModel";
import { lightenDarkenColor } from "../mixins/lightenDarkenColor";
import icon from "../assets/svg/Icon";

const baseurl = process.env.VUE_APP_BASEURL;
var Color = require("color");

export default {
  name: "Form",
  components: {
    FlowForm,
    icon
  },
  data() {
    return {
      submitted: false,
      completed: false,
      language: new LanguageModel(),
      // Create question list with QuestionModel instances
      questions: [],
      design: {},
      endPage: {},
      backgroundUrl: "",
      formTitle: "",
      isLoading: true,
      acceptingResponse: true,
      teamId: "",
      formId: "",
      question: "",
      isUploadingFile: false,
      isUploadingError: false,
      displayLogo: true,
      template: "scratch",
      username: "",
      showUsername: false,
      totalScore: 0,
      totalCorrectAnswer: 0,
      totalWrongAnswer: 0
    };
  },
  async beforeMount() {
    const pathArray = window.location.pathname.split("/");
    const formId = pathArray[pathArray.length - 1];
    this.formId = formId;
    let url;
    if (this.isPreview) {
      url = `${baseurl}/form/${formId}/preview`;
    } else {
      url = `${baseurl}/form/${formId}`;
    }
    let form;
    try {
      const res = await fetch(url, {
        method: "GET"
      });
      if (!res.ok) {
        this.isLoading = false;
        const message = await res.json()
        if (message.error === 'Response limit already reached.') {
          this.acceptingResponse = false;
        }

        return;
      }
      form = await res.json();
    } catch (error) {
      this.isLoading = false;
    }
    if (Object.keys(form).length === 0 && form.constructor === Object) {
      return;
    }
    this.questions = form.fields.map(
      field =>
        new QuestionModel({
          ...field,
          multiple:
            field.allowMultipleAnswer || field.answerOptions === "multiple",
          type: this.buildQuestionType(field.type),
          options: field.options
            ? field.options.map(option => this.buildOption(option))
            : null
        })
    );
    this.isLoading = false;
    this.acceptingResponse = form.acceptingResponse;
    this.design = form.design;
    this.endPage = form.endPage;
    this.formTitle = form.title;
    this.question = form.question;
    this.teamId = form.teamId;
    this.displayLogo = form.displayLogo;
    this.template = form.template || "scratch";
    let title = document.createElement("title");
    title.innerHTML = this.formTitle;
    document.getElementsByTagName("head")[0].appendChild(title);
    let css = document.createElement("style");
    css.type = "text/css";
    let scaleTextColor;
    const opinionScaleColorBg = Color(this.design.colors.answerColor).fade(0.9);
    const opinionScaleHover = Color(this.design.colors.answerColor).fade(0.8);
    const answerColorContrast = Math.round(
      Color(this.design.colors.answerColor).contrast(Color("#ffffff"))
    );
    if (answerColorContrast > 4) {
      scaleTextColor = "#ffffff";
    } else {
      scaleTextColor = "#000000";
    }
    if (form.useBackground) {
      this.backgroundUrl = `url(${form.backgroundUrl})`;
    }
    let styles = `
        :root {
      --vff-bg-color: ${this.design.colors.backgroundColor};
      --vff-bg-image: ${this.backgroundUrl};
      --vff-title-color: ${this.design.colors.titleColor};
      --vff-description-color: ${this.design.colors.descriptionColor};
      --vff-answer-color: ${this.design.colors.answerColor};
      --vff-opinion-scale: ${opinionScaleColorBg};
      --vff-opinion-scale-hover: ${opinionScaleHover};
      --vff-answer-darken-color: ${lightenDarkenColor(
        this.design.colors.answerColor,
        -20
      )};
      --vff-answer-lighten-color: ${lightenDarkenColor(
        this.design.colors.answerColor,
        10
      )};
      --vff-answer-bg-color: ${lightenDarkenColor(
        this.design.colors.answerColor,
        -40
      )};
      --vff-button-color: ${this.design.colors.buttonColor};
      --vff-button-text-color: ${this.design.colors.buttonTextColor};
      --vff-main-text-color: '#000000';
      --vff-secondary-text-color: '#000000';
      --vff-tertiary-text-color: #f1f1f1;
      --vff-main-accent-color: #0DBC79;
      --vff-secondary-accent-color: #BBEBD5;
      --vff-scale-text-color: ${scaleTextColor};
      }`;
    if (css.styleSheet) {
      css.styleSheet.cssText = styles;
    } else {
      css.appendChild(document.createTextNode(styles));
    }
    document.getElementsByTagName("head")[0].appendChild(css);
  },
  mounted() {
    document.addEventListener("keyup", this.onKeyListener);
  },
  beforeDestroy() {
    document.removeEventListener("keyup", this.onKeyListener);
  },
  computed: {
    isPreview() {
      return new URL(location.href).searchParams.get("preview") === "true";
    }
  },
  methods: {
    onKeyListener($event) {
      // We've overriden the default "complete" slot so
      // we need to implement the "keyup" listener manually.

      if ($event.key === "Enter" && this.completed && !this.submitted) {
        this.onSendData();
      }
    },

    /* eslint-disable-next-line no-unused-vars */
    onComplete(completed, questionList) {
      // This method is called whenever the "completed" status is changed.
      this.completed = completed;
    },

    /* eslint-disable-next-line no-unused-vars */
    onSubmit(questionList) {
      // This method will only be called if you don't override the
      // completeButton slot.
      this.onSendData();
    },

    async getSignedUrl(fileName, fileType) {
      const result = await fetch(`${baseurl}/files/signed-url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          teamId: this.teamId,
          formId: this.formId,
          fileType,
          fileName
        })
      });
      return result;
    },

    async onSendData() {
      this.isUploadingError = false;
      try {
        // check for file uploads
        const uploadFileQuestions = this.questions.filter(
          question =>
            question.type === QuestionType.FileUpload &&
            question.answered === true
        );

        if (uploadFileQuestions.length > 0) {
          this.isUploadingFile = true;
          await Promise.all(
            uploadFileQuestions.map(async question => {
              const files = await Promise.all(
                question.files.map(async file => {
                  const res = await this.getSignedUrl(file.name, file.type);
                  const data = await res.json();
                  await fetch(data.uploadURL, {
                    method: "PUT",
                    body: file
                  });

                  return {
                    fileName: file.name,
                    fileType: file.type,
                    key: data.Key
                  };
                })
              );

              const updateQuestion = this.questions.find(
                q =>
                  q.type === QuestionType.FileUpload &&
                  q.title === question.title
              );
              updateQuestion.files = files;
            })
          );
          this.isUploadingFile = false;
        }

        const pathArray = window.location.pathname.split("/");
        const formId = pathArray[pathArray.length - 1];
        const data = this.getData();
        if (this.isPreview) {
          return;
        }

        fetch(`${baseurl}/form/${formId}/response`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(data)
        });

        // Set `submitted` to true so the form knows not to allow back/forward
        // navigation anymore.
        this.$refs.flowform.submitted = true;

        this.submitted = true;
      } catch (error) {
        this.isUploadingError = true;
      }
    },

    getData() {
      const data = {
        createdAt: Date.now(),
        teamId: this.teamId,
        username: this.username,
        totalScore: this.totalScore,
        totalCorrectAnswer: this.totalCorrectAnswer,
        totalWrongAnswer: this.totalWrongAnswer,
        responses: this.questions.map(question => ({
          id: question.id,
          answer: question.answer,
          type: question.type,
          title: question.title,
          description: question.description,
          options: question.options,
          label: question.label,
          question: question.question,
          correctAnswer: question.correctAnswer,
          maxScore: question.maxScore,
          answerScore: question.answerScore,
          allowMultipleAnswer:
            question.allowMultipleAnswer ||
            question.answerOptions === "multiple" ||
            null,
          selectedCountry: question.selectedCountry || null,
          files: question.files || null,
          scaleStep: question.scaleStep
        }))
      };
      return data;
    },

    buildQuestionType(type) {
      switch (type) {
        case "START_PAGE":
          return QuestionType.StartPage;

        case "STATEMENT":
          return QuestionType.Statement;

        case "EMAIL":
          return QuestionType.Email;

        case "TEL":
          return QuestionType.Phone;

        case "SCALE":
          return QuestionType.Scale;

        case "MULTISELECT":
          return QuestionType.MultipleChoice;

        case "LONG_TEXT":
          return QuestionType.LongText;

        case "SHORT_TEXT":
          return QuestionType.ShortText;

        case "DROPDOWN":
          return QuestionType.Dropdown;

        case "DATE":
          return QuestionType.DropdownDate;

        case "NUMBER":
          return QuestionType.Number;

        case "FILE_UPLOAD":
          return QuestionType.FileUpload;

        case "MULTISELECT_QUIZ":
          return QuestionType.MultipleChoiceQuiz;

        case "TRUE_FALSE":
          return QuestionType.TrueFalse;

        default:
          return QuestionType.SectionBreak;
      }
    },

    buildOption(option) {
      return new ChoiceOption({
        label: option.label ?? null,
        value: option.value ?? null,
        file: option.file ?? null
      });
    },

    showFlowform() {
      if (
        this.username.length &&
        this.template === "quiz" &&
        this.showUsername
      ) {
        this.showUsername = false;
      }
    },

    addScorePoint(event) {
      this.totalScore += event;
    },

    addTotalCorrectAnswer(event) {
      this.totalCorrectAnswer += event;
    },

    addTotalWrongAnswer(event) {
      this.totalWrongAnswer += event;
    }
  },
  watch: {
    template: {
      immediate: true,
      handler() {
        if (this.template === "quiz") {
          this.showUsername = true;
        } else {
          this.showUsername = false;
        }
      }
    }
  }
};
</script>

<style lang="css">
@import "../../src/assets/css/themes/theme-survein.css";
@import "../assets/css/common.css";

.accept-link {
  position: absolute;
  bottom: 0;
  width: 100%;
  text-align: center;
}

.accept-link a {
  text-decoration: none;
  cursor: pointer;
  color: #3881d7;
}

.loader {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  background-color: white;
  z-index: 1;
}

.loader img {
  width: 25px;
  height: 25px;
}

.username-quiz {
  display: flex;
  flex-direction: column;
  max-width: 300px;
  height: 100vh;
  justify-content: center;
  align-items: center;
  margin: auto;
}

.name-quiz {
  margin-bottom: 16px;
  font-weight: bold;
  font-size: 24px;
}

.o-btn-action {
  margin-top: 16px;
  padding: 6px 64px;
  border: none;
  border-radius: 4px;
  z-index: 1;
  line-height: 1.2;
  cursor: pointer;
}

.o-btn-action span {
  font-size: 16px;
  user-select: none;
  text-decoration: none;
  transition: all 0.4s ease 0s;
}

.username-input {
  padding: 8px;
  border: 1px solid #166ed83b;
  border-radius: 4px;
  width: 180px;
  text-align: center;
}

.score-wrapper {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px;
  border-radius: 4px;
  width: 60px;
  background: #166ed8;
  color: white;
}

.score-wrapper span {
  margin-left: 8px;
}
</style>
